import { useCallback, useEffect, useRef } from "react";
import { useLocalStorage } from "react-use";

/**
 * Enhanced version of useLocalStorage that supports function updater like useState
 *
 * @example
 * const [value, setValue] = useLocalStorageWithUpdater('my-key', { count: 0 });
 *
 * // Direct value
 * setValue({ count: 1 });
 *
 * // Function updater (prevents race conditions)
 * setValue(prev => ({ count: prev.count + 1 }));
 */
export const useLocalStorageWithUpdater = <T>(
  key: string,
  initialValue: T
): [T | undefined, (updaterOrValue: T | ((prev: T) => T)) => void] => {
  const [value, setValue] = useLocalStorage<T>(key, initialValue);
  const valueRef = useRef(value);

  // Keep ref in sync with the latest value
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const setValueWithUpdater = useCallback(
    (updaterOrValue: T | ((prev: T) => T)) => {
      // Get the current value from ref to avoid stale closure
      const currentValue = valueRef.current ?? initialValue;

      const newValue =
        typeof updaterOrValue === "function"
          ? (updaterOrValue as (prev: T) => T)(currentValue)
          : updaterOrValue;

      // Update both ref and localStorage
      valueRef.current = newValue;
      setValue(newValue);
    },
    [setValue, initialValue]
  );

  return [value, setValueWithUpdater];
};
